import mapboxgl, { Map } from "mapbox-gl";
import {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { MapContext } from "./MapContext";

import locationGray from "@/assets/map/location-gray.png";
import locationGreen from "@/assets/map/location-green.png";
import locationRed from "@/assets/map/location-red.png";
import locationWhite from "@/assets/map/location-white.png";
import locationYellow from "@/assets/map/location-yellow.png";
import { debounce } from "lodash";
import { getMapboxToken, isApiTileRequestUrl } from "@/lib/utils/map";

function apiRequestHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    const token = window.localStorage.getItem("auth_token");
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    const timeTravel = sessionStorage.getItem("time-travel");
    if (timeTravel) {
        headers["Time-Travel"] = timeTravel;
    }
    const unitHeader = localStorage.getItem("unit_preference");
    if (unitHeader) {
        headers["N-Time-Unit-Type"] = unitHeader;
    }
    return headers;
}

export function MapProvider({ children }: { children: ReactNode }) {
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<Map | null>(null);
    const geolocateControl = useRef<mapboxgl.GeolocateControl | null>(null);
    const [mapboxLoaded, setMapboxLoaded] = useState(false);
    const [styleLoaded, setStyleLoaded] = useState(false);
    const resizeObserver = useRef<ResizeObserver | null>(null);

    const mapLoaded = useMemo(
        () => mapboxLoaded && styleLoaded,
        [mapboxLoaded, styleLoaded],
    );

    const initializeMap = useCallback(() => {
        if (!mapContainerRef.current) return;

        mapRef.current = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: "mapbox://styles/mapbox/satellite-streets-v11",
            center: [-96.7026, 40.8136],
            zoom: 8,
            accessToken: getMapboxToken(),
            transformRequest: (url) => {
                if (!isApiTileRequestUrl(url)) {
                    return { url };
                }
                return {
                    url,
                    headers: apiRequestHeaders(),
                };
            },
        });

        mapRef.current.on("load", () => {
            setMapboxLoaded(true);

            const icons = {
                "location-gray": locationGray,
                "location-green": locationGreen,
                "location-red": locationRed,
                "location-yellow": locationYellow,
                "location-white": locationWhite,
            };

            Object.entries(icons).forEach(async ([name, img]) => {
                if (!mapRef.current) return;
                mapRef.current.loadImage(img, (error, image) => {
                    if (!mapRef.current) return;
                    if (error) throw error;

                    if (!mapRef.current.hasImage(name) && image) {
                        mapRef.current.addImage(name, image);
                    }
                });
            });
        });

        geolocateControl.current = new mapboxgl.GeolocateControl({
            positionOptions: {
                enableHighAccuracy: true,
            },
            trackUserLocation: true,
            showUserLocation: true,
        });

        mapRef.current.addControl(geolocateControl.current);

        mapRef.current.on("style.load", () => {
            setStyleLoaded(true);
        });
        return () => {
            if (mapRef.current) {
                setMapboxLoaded(false);
                setStyleLoaded(false);
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!mapLoaded || !mapContainerRef.current) return;

        const debouncedResize = debounce(() => {
            if (mapRef.current) {
                mapRef.current.resize();
            }
        }, 1);

        resizeObserver.current = new ResizeObserver(() => {
            if (!mapRef.current) return;

            debouncedResize();
        });

        resizeObserver.current.observe(mapContainerRef.current);

        return () => {
            resizeObserver.current?.disconnect();
        };
    }, [mapLoaded]);

    return (
        <MapContext.Provider
            value={{
                map: mapRef.current,
                mapContainerRef,
                mapLoaded,
                initializeMap,
            }}
        >
            {children}
        </MapContext.Provider>
    );
}
